import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { EvidenceItem, IntervieweeCard } from "../../../domain/contracts";
import type { RouteSelectionController } from "../../../hooks/useRouteSelection";
import { useProtectedMedia } from "../../../hooks/useProtectedMedia";
import { intervieweeDetailQueryOptions } from "../../../services/queries/publicQueryOptions";
import { buildMediaPreviewItems } from "../../../services/viewModels";

type IntervieweeControllerArgs = {
  routeSelection: RouteSelectionController;
  interviewees: IntervieweeCard[];
};

export function useIntervieweeController({ routeSelection, interviewees }: IntervieweeControllerArgs) {
  useEffect(() => {
    if (!routeSelection.intervieweeRef || interviewees.length === 0) {
      return;
    }

    const hasMatchingInterviewee = interviewees.some(
      (item) => item.id === routeSelection.intervieweeRef || item.responseId === routeSelection.intervieweeRef,
    );
    if (!hasMatchingInterviewee) {
      routeSelection.setIntervieweeRef(null);
    }
  }, [interviewees, routeSelection]);

  const selectedInterviewee = useMemo(
    () => interviewees.find((item) => item.id === routeSelection.intervieweeRef || item.responseId === routeSelection.intervieweeRef) ?? null,
    [interviewees, routeSelection.intervieweeRef],
  );

  const intervieweeDetailQuery = useQuery(intervieweeDetailQueryOptions(selectedInterviewee?.id ?? null));

  const detailEvidence = useMemo<EvidenceItem[]>(() => {
    const villageId = intervieweeDetailQuery.data?.interviewee?.villageId ?? selectedInterviewee?.villageId ?? "";
    const intervieweeId = intervieweeDetailQuery.data?.interviewee?.id ?? selectedInterviewee?.id ?? null;

    return (intervieweeDetailQuery.data?.interviewee?.evidence ?? []).map((item, index) => ({
      id: item.id,
      villageId,
      intervieweeId,
      category: item.category,
      mediaType: item.mediaType,
      mimeType: item.mimeType,
      captionDefault: item.captionDefault,
      translationStatus: "available",
      redactionStatus: item.redactionStatus,
      assetId: item.assetId,
      branchKind: item.branchKind ?? null,
      itemIndex: item.itemIndex ?? null,
      detailIndex: item.detailIndex ?? null,
      slotType: item.slotType ?? null,
      slotLabel: item.slotLabel ?? null,
      groupLabel: item.groupLabel ?? null,
      sortOrder: index,
      publishVersion: intervieweeDetailQuery.data?.version ?? "preview-detail",
    }));
  }, [intervieweeDetailQuery.data?.interviewee, intervieweeDetailQuery.data?.version, selectedInterviewee?.id, selectedInterviewee?.villageId]);

  const mediaObjectUrls = useProtectedMedia(detailEvidence, {
    enabled: Boolean(selectedInterviewee),
    limit: Math.max(detailEvidence.length, 1),
  });
  const mediaPreviews = useMemo(
    () => buildMediaPreviewItems(detailEvidence, mediaObjectUrls, Math.max(detailEvidence.length, 1)),
    [detailEvidence, mediaObjectUrls],
  );

  return {
    selectedInterviewee,
    intervieweeDetail: intervieweeDetailQuery.data?.interviewee ?? null,
    mediaPreviews,
    loading: {
      isDetailLoading: intervieweeDetailQuery.isLoading,
    },
  };
}